import { ExclamationTriangleIcon } from "@heroicons/react/24/solid"
import { Tooltip } from "react-tooltip"
import "react-tooltip/dist/react-tooltip.css"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/**
 * Exclamation icon shown next to a KPI cell value whose composite metric is
 * missing one of its source components for that period (see the
 * `week_N_warning` / `week_N_warning_prev` row keys attached by the KPI page's
 * row builders via ~/shared/kpi data-availability).
 *
 * A table can flag hundreds of cells at once (that IS the target scenario —
 * e.g. a short Amazon history flags most weekly columns), so each icon is only
 * a lightweight tooltip ANCHOR; the table renders one shared
 * `AvailabilityWarningTooltip` that all anchors bind to by id.
 */
export default function AvailabilityWarningIcon({
  tooltipId,
  message,
  className,
}: {
  tooltipId: string
  message: string
  className?: string
}) {
  return (
    <span
      className={cn("inline-flex align-middle", className)}
      data-tooltip-id={tooltipId}
      data-tooltip-content={message}
    >
      <ExclamationTriangleIcon
        aria-label="Data availability warning"
        data-testid="availability-warning-icon"
        className="w-3.5 h-3.5 text-system-warning-400 cursor-help"
      />
    </span>
  )
}

/**
 * The single shared tooltip instance the warning icons anchor to. Styled to
 * match RichTooltip; content comes from each anchor's `data-tooltip-content`.
 */
export function AvailabilityWarningTooltip({ id }: { id: string }) {
  return (
    <Tooltip
      id={id}
      border="2px solid #222250"
      opacity="1"
      place="top"
      className="!bg-system-blue-800 !border !border-[2px] !border-system-blue-700 !p-system-md !rounded-system-sm !text-basic-white !text-sm !font-normal normal-case !text-left z-[999999] !max-w-xs !whitespace-normal !break-words"
      classNameArrow="!bg-system-blue-800"
      delayShow={200}
      delayHide={500}
      positionStrategy="fixed"
    />
  )
}
